import { ErrorHandler, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { compact, uniq } from 'lodash';

import * as docuri from 'docuri';

import { DbTypeActionMapping } from './db.tokens';
import { DocumentWithId, DocumentWithRev, DocumentWithType } from './models/document-with-type';

import ChangesResponseChange = PouchDB.Core.ChangesResponseChange;

const PouchMirror = require('pouch-mirror');
const PouchDB = require('pouchdb').default;
const PouchDBFind = require('pouchdb-find').default;
const InMemoryAdapter = require('pouchdb-adapter-memory').default;


interface ActionMapping {
	[s: string]: {
		route: (doc: { [s: string]: any}) => string;
		create: Array<string>;
		update: Array<string>;
		remove: Array<string>;
		load: Array<string>;
		db?: string;
	};
}

interface Replicator {
	start: () => {};
	pause: () => {};
	on: (e: string, cb: (e: any) => void) => {};
}


@Injectable()
export class DbService {
	private _mirrors: { [s: string]: PouchDB.Database } = {};
	private _locals: { [s: string]: PouchDB.Database } = {};
	private _replicators: { [s: string]: Replicator } = {};

	private _remotes: { [s: string]: PouchDB.Database } = {};
	private _initialized: { [s: string]: string } = {};

	private actionMappings: ActionMapping = {};
	private idToTypeMapping: Map<string, string> = new Map();
	private _loadQueue: { [s: string]: Array<string> } = {};
	private _loadedQueue: { [s: string]: Array<string> } = {};

	constructor(private _store: Store<{}>, private _errorHandler: ErrorHandler) {
		PouchDB.plugin(PouchDBFind);
		PouchDB.plugin(InMemoryAdapter);
	}

	public initDB(url: string, alias: string) {
		if (this._initialized[alias]) {
			if (this._initialized[alias] === url) {
				return;
			}

			this._initialized[alias] = '';
			this.removeDB(alias).then(() => {
				this._loadQueue[alias] = this._loadedQueue[alias];
				this._loadedQueue[alias] = [];
				this.initDB(url, alias);
			});

			return;
		}


		this._initialized[alias] = url;
		this._remotes[alias] = new PouchDB(url);
		this._createIndices(alias);

		this._locals[alias] = new PouchDB(alias, {adapter: 'memory'});
		const mirror = new PouchMirror(this._locals[alias], this._remotes[alias]);
		this._mirrors[alias] = (mirror as PouchDB.Database);
		this._replicators[alias] = mirror.start({retry: true});

		this._replicators[alias].on('up-to-date', () => {
			this.getDB(alias).allDocs({include_docs: true}).then(result => {
				result.rows.forEach(row => {
					if (row.doc && row.doc._id && row.doc['type']) {
						this.idToTypeMapping.set(row.doc._id, row.doc['type']);
					}
				});
			});

			this.getDB(alias).changes({since: 'now', live: true, include_docs: true})
				.on('change', change => this._onChange(change))
				.on('error', err => this._onError(err));

			this._loadQueue[alias].forEach(q => {
				this._loadDocumentsByType(q, alias).then(documents => {
					this._dispatchToStore(documents, q, 'load');
				});
			});

			this._loadedQueue[alias] = this._loadQueue[alias];
			this._loadQueue[alias] = [];
		});
	}

	public getDB(alias: string): PouchDB.Database | undefined {
		return this._mirrors[alias];
	}

	public removeDB(alias: string): Promise<void> {
		return this.getDB(alias).destroy().then(() => {
			this._locals[alias] = null;
			this._remotes[alias] = null;
			this._mirrors[alias] = null;
			this._replicators[alias] = null;
		});
	}

	public registerActions(mapping: Array<DbTypeActionMapping>) {
		mapping.forEach(map => {
			if (this.actionMappings[map.type]) {
				return;
			}

			if (!this.actionMappings[map.type]) {
				this.actionMappings[map.type] = {
					route: docuri.route(map.route),
					create: [],
					update: [],
					remove: [],
					load: [],
					db: map.db || 'userDB'
				};
			}

			if (map.create) {
				this.actionMappings[map.type].create.push(map.create);
			}

			if (map.update) {
				this.actionMappings[map.type].update.push(map.update);
			}

			if (map.remove) {
				this.actionMappings[map.type].remove.push(map.remove);
			}

			if (map.load) {
				this.actionMappings[map.type].load.push(map.load);
			}

			this._loadDocumentsByType(map.type, map.db).then(documents => {
				documents.forEach(doc => this._dispatchToStore(doc, doc['type'], 'create'));
			});
		});
	}

	public put(doc: DocumentWithType): Observable<PouchDB.Core.Response> {
		if (!doc._id) {
			doc._id = this.actionMappings[doc.type].route(doc);
		}

		return Observable.fromPromise(this._mirrors[this.actionMappings[doc.type].db].put(doc).then(result => {
			this.idToTypeMapping.set(result.id, doc.type);
			return result;
		}));
	}

	public remove(doc: DocumentWithId & DocumentWithRev): Observable<PouchDB.Core.Response> {
		return Observable.fromPromise(this._mirrors[this.actionMappings[doc.type].db].remove(doc).then(result => {
			this.idToTypeMapping.delete(doc._id);
			return result;
		}));
	}

	public get(documentId: string, alias?: string): Observable<PouchDB.Core.Document<{}> & PouchDB.Core.GetMeta> {
		if (alias) {
			return Observable.fromPromise(this.getDB(alias).get(documentId));
		}

		return Observable.fromPromise(
			Promise.all(this._getAllAliases().map(dbAlias => this.get(documentId, dbAlias)))
				.then(results => compact([].concat(...results))[0])
		);
	}

	public bulkDocs(doc: Array<DocumentWithId & DocumentWithRev>, alias?: string): Observable<Array<PouchDB.Core.Response | PouchDB.Core.Error>> {
		if (alias) {
			return Observable.fromPromise(this.getDB(alias).bulkDocs(doc).then(result => {
				doc.forEach(d => {
					if (d['_deleted']) {
						this.idToTypeMapping.delete(d._id);
					} else {
						if (d['type']) {
							this.idToTypeMapping.set(d._id, d['type']);
						}
					}
				});

				return result;
			}));
		}

		return Observable.fromPromise(
			Promise.all(this._getAllAliases().map(dbAlias => this.bulkDocs(doc, dbAlias)))
				.then(results => [].concat(...results))
		);
	}

	private _dispatchToStore(document: PouchDB.Core.ChangesMeta | PouchDB.Core.ChangesMeta[], type: string, kindOfEvent: 'create' | 'update' | 'remove' | 'load') {
		if (!type || !this.actionMappings[type]) {
			return;
		}

		(this.actionMappings[type][kindOfEvent] || []).forEach(actionType => {
			this._store.dispatch({type: actionType, payload: document});
		});
	}

	private _createIndices(alias: string) {
		if (this._remotes[alias]) {
			this._remotes[alias].getIndexes().then(response => {
				if (!response.indexes.find(index => index.name === 'by_type')) {
					this._remotes[alias].createIndex({
						index: {fields: ['type'], name: 'by_type', ddoc: 'by_type'}
					}).catch(err => this._onError(err));
				}
			});
		}
	}

	private _loadAllDocumentsByType(type: string): Promise<Array<PouchDB.Core.ChangesMeta>> {
		const getPromise = (alias: string): Promise<Array<PouchDB.Core.ChangesMeta>> => {
			if (!this.getDB(alias)) {
				return this._populateQueue(type, alias);
			}

			return this._loadDocumentsByType(type, alias);
		};

		return Promise
			.all(this._getAllAliases().map(alias => getPromise(alias)))
			.then((responses: Array<Array<PouchDB.Core.ChangesMeta>>) => {
				return [].concat(...responses);
			});
	}

	private _loadDocumentsByType(type: string, alias?: string): Promise<Array<PouchDB.Core.ChangesMeta>> {
		if (!alias) {
			return this._loadAllDocumentsByType(type);
		}

		if (!this.getDB(alias)) {
			return this._populateQueue(type, alias);
		}

		return this.getDB(alias)['find']({selector: {type: type}})
			.then(response => response.docs).catch(() => []);
	}

	private _populateQueue(type: string, alias: string): Promise<Array<PouchDB.Core.ChangesMeta>> {
		if (!this._loadQueue[alias]) {
			this._loadQueue[alias] = [];
		}

		this._loadQueue[alias].push(type);
		return Promise.resolve([]);
	}

	private _onChange(change: ChangesResponseChange<any>) {
		if (!change.doc['type']) {
			if (!this.idToTypeMapping.has(change.doc._id)) {
				return;
			}

			change.doc['type'] = this.idToTypeMapping.get(change.doc._id);
		}

		if (!this.actionMappings[change.doc['type']]) {
			return;
		}

		switch (true) {
			case change.deleted:
				this._dispatchToStore(change.doc, change.doc['type'], 'remove');
				break;

			case change.doc._rev.split('-')[0] === '1':
				this._dispatchToStore(change.doc, change.doc['type'], 'create');
				break;

			default:
				this._dispatchToStore(change.doc, change.doc['type'], 'update');
				break;
		}
	}

	private _onError(err: any) {
		this._errorHandler.handleError(err);
	}

	private _getAllAliases(): Array<string> {
		return uniq(Object.keys(this.actionMappings).map(key => this.actionMappings[key].db));
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/services/db/db.service.ts